/**
 * @mhmo91/ican — `entity().pipe(...)`.
 *
 * Compile-once: `pipe` invokes the user's builder once with a typed
 * `{ when, and, or, not }` context, then runs each returned rule's
 * condition callback exactly once with fresh recording proxies. The
 * resulting `Cond` AST is deep-frozen onto the rule. Subsequent
 * `me.can(...)` checks walk the frozen AST against the *real*
 * `(data, me)` pair — no Proxy on the hot path, no allocation per
 * check.
 *
 * Curried call shape:
 *
 *     entity<User>()({ schema, actions: ['view', 'edit'] }).pipe(
 *       ({ when, and, not }) => [
 *         when((data, me) => data.orgId.eq(me.orgId)).allow('view'),
 *         when((data, me) => and(
 *           data.orgId.eq(me.orgId),
 *           data.status.eq('draft'),
 *         )).allow('edit'),
 *       ],
 *     );
 *
 * The curry pins `User` while letting TypeScript infer `Schema` and
 * the literal `Actions` tuple from the seed. The builder closure
 * gives the rule author a `when` whose proxies are typed against the
 * entity — TS doesn't support contextual-type flow through method
 * chains, so the closure is the cleanest way to keep `data.foo` /
 * `me.role` narrowed inside each rule.
 */
import type { z } from 'zod';
import { makeCan } from './can.ts';
import { and, not, or } from './combinators.ts';
import { hasPermission, member, memberOf, ownRole } from './permissions.ts';
import { makeParentRoot, makePathVarRef, makeRoot, makeWorkspaceRoot } from './proxy.ts';
import {
  type UserBase,
  type AdapterBinding,
  type Cond,
  ENTITY_BRAND,
  type Entity,
  type EntityBuilder,
  type EntitySeed,
  type RejectIndexSignature,
  type Rule,
  type TranspileError,
} from './types.ts';
import { finalizePendingWritePattern, isPendingRule, makeWhen, type PendingRule } from './when.ts';

const deepFreeze = <T>(value: T): T => {
  if (value === null || typeof value !== 'object') return value;
  if (Object.isFrozen(value)) return value;
  Object.freeze(value);
  for (const key of Object.keys(value as object)) {
    deepFreeze((value as Record<string, unknown>)[key]);
  }
  return value;
};

/**
 * `entity<User>()`'s return type. `RejectIndexSignature<User>` maps an
 * index-signed actor to a branded `TranspileError` and any fixed-key
 * actor to itself; this conditional type carries that resolution
 * straight into the return position, so `entity<Actor>()` itself always
 * type-checks (there is no phantom parameter to fail a constraint at
 * the declaration) and the guard fires only when the returned value is
 * *invoked* — `entity<IndexedActor>()(seed)` — because the returned
 * `TranspileError` object has no call signature.
 */
type EntityFactoryOrReject<User extends UserBase> =
  RejectIndexSignature<User> extends TranspileError<string>
    ? RejectIndexSignature<User>
    : <Schema extends z.ZodTypeAny, const Actions extends readonly string[]>(seed: {
        readonly schema: Schema;
        readonly actions: Actions;
        readonly namespace?: string;
      }) => EntitySeed<User, Actions, Schema>;

/**
 * Curried entity factory. The outer call pins `User`; the inner call
 * accepts the seed (`schema` + `actions`) and returns an
 * `EntitySeed` whose `.pipe(...)` finalizes the rules.
 *
 * The index-signature guard lives on the RETURN type
 * (`EntityFactoryOrReject`), not a phantom parameter: an index-signed
 * actor makes `entity<Actor>()` resolve to a `TranspileError` value, so
 * `entity<Actor>()` still compiles but *invoking* it — `entity<Actor>()(seed)`
 * — fails, because a `TranspileError` object has no call signature. An
 * index signature would otherwise silently widen `keyof User` and
 * defeat typo detection on `me.X`.
 */
export function entity<User extends UserBase = UserBase>(): EntityFactoryOrReject<User> {
  const factory = <Schema extends z.ZodTypeAny, const Actions extends readonly string[]>(seed: {
    readonly schema: Schema;
    readonly actions: Actions;
    readonly namespace?: string;
  }): EntitySeed<User, Actions, Schema> => ({
    schema: seed.schema,
    actions: seed.actions,
    ...(seed.namespace !== undefined ? { namespace: seed.namespace } : {}),
    pipe: (
      build: (
        ctx: EntityBuilder<User, Actions, Schema>,
      ) => ReadonlyArray<Rule<User, z.infer<Schema>, Actions[number]>>,
    ): Entity<User, Actions, Schema> => {
      const builder: EntityBuilder<User, Actions, Schema> = {
        when: makeWhen<User, Schema, Actions>(),
        can: makeCan<User, Schema, Actions>(),
        and,
        or,
        not,
        dataExists: () => ({ kind: 'dataExists' }),
        hasPermission,
        member,
        pathOwner: (variable, prefix = '') => ({ kind: 'pathOwner', variable, prefix }),
        memberOf,
        ownRole,
        workspace: makeWorkspaceRoot,
        parent: makeParentRoot,
        parentCan: (name, parent, action) => ({
          kind: 'parentAccess', parent: name,
          rules: parent.rules.filter((rule) => rule.actions.includes(action)).map((rule) => ({ effect: rule.effect, cond: rule.cond })),
        }),
        pathVar: makePathVarRef,
      };
      const pendingRules = build(builder);
      const dataProxy = makeRoot('data');
      const meProxy = makeRoot('me');
      const rules = pendingRules.map((r) => {
        if (!isPendingRule(r)) {
          throw new Error(
            '@mhmo91/ican: every rule returned from pipe(...) must be `when(...).allow(...)`, `when(...).deny(...)`, or `can(...).when(...)`',
          );
        }
        const pending = r as unknown as PendingRule<User, Schema, Actions[number]>;
        const cond = deepFreeze<Cond>(
          pending._callback(
            dataProxy as Parameters<typeof pending._callback>[0],
            meProxy as Parameters<typeof pending._callback>[1],
          ),
        );
        const writePattern = pending.pendingWritePattern
          ? finalizePendingWritePattern<Schema>(
              pending.pendingWritePattern,
              dataProxy as Parameters<
                NonNullable<typeof pending.pendingWritePattern> extends {
                  preStateCallback?: infer P;
                }
                  ? NonNullable<P>
                  : never
              >[0],
              deepFreeze,
            )
          : undefined;
        return Object.freeze({
          effect: pending.effect,
          actions: pending.actions,
          cond,
          ...(writePattern ? { writePattern } : {}),
          ...(typeof pending.handlerName === 'string' ? { handlerName: pending.handlerName } : {}),
        }) as Rule<User, z.infer<Schema>, Actions[number]>;
      });
      const frozenRules = Object.freeze(rules) as ReadonlyArray<
        Rule<User, z.infer<Schema>, Actions[number]>
      >;
      return makeEntity<User, Actions, Schema>(
        seed.schema,
        seed.actions,
        frozenRules,
        [],
        seed.namespace,
      );
    },
  });
  return factory as unknown as EntityFactoryOrReject<User>;
}

/**
 * Build an immutable entity carrying its rules + adapter bindings.
 * `.adaptor(binding)` returns a new entity with the binding appended;
 * the source entity is unchanged. The kernel never inspects
 * `_adapters` — adapters filter to their own `_adapter` discriminator
 * at emit time.
 */
const makeEntity = <
  User extends UserBase,
  Actions extends readonly string[],
  Schema extends z.ZodTypeAny,
>(
  schema: Schema,
  actions: Actions,
  rules: ReadonlyArray<Rule<User, z.infer<Schema>, Actions[number]>>,
  adapters: ReadonlyArray<AdapterBinding>,
  namespace?: string,
): Entity<User, Actions, Schema> => {
  const e = {
    [ENTITY_BRAND]: true as const,
    schema,
    actions,
    ...(namespace !== undefined ? { namespace } : {}),
    rules,
    _adapters: Object.freeze([...adapters]) as ReadonlyArray<AdapterBinding>,
    adaptor(binding: AdapterBinding): Entity<User, Actions, Schema> {
      return makeEntity<User, Actions, Schema>(
        schema,
        actions,
        rules,
        [...adapters, binding],
        namespace,
      );
    },
  };
  return Object.freeze(e);
};

export const isEntity = (
  value: unknown,
): value is Entity<UserBase, readonly string[], z.ZodTypeAny> =>
  typeof value === 'object' && value !== null && ENTITY_BRAND in value;
